import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CategorySlug } from "@mestergo/shared";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { useLocale } from "../lib/i18n";
import { api } from "../lib/api";
import { getTelegramWebApp } from "../lib/telegram";

interface PortfolioCase {
  id: string;
  title: string;
  description: string;
  locationText?: string | null;
  completedAt?: string | null;
  category?: {
    slug: CategorySlug;
    nameRo: string;
    nameRu?: string | null;
  } | null;
  media: Array<{
    id: string;
    fileUrl: string;
  }>;
}

interface PresignResponse {
  uploadUrl: string;
  publicUrl: string;
}

interface Lightbox {
  images: string[];
  index: number;
}

const CATEGORY_OPTIONS: CategorySlug[] = [
  CategorySlug.Plumber,
  CategorySlug.Electrician,
  CategorySlug.ApplianceRepair,
  CategorySlug.Cleaning,
  CategorySlug.Moving,
  CategorySlug.Other,
];

export function HandymanPortfolioPage() {
  const navigate = useNavigate();
  const { locale, t } = useLocale();
  const [cases, setCases] = useState<PortfolioCase[]>([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [locationText, setLocationText] = useState("");
  const [completedAt, setCompletedAt] = useState("");
  const [categorySlug, setCategorySlug] = useState<CategorySlug>(CategorySlug.Electrician);
  const [files, setFiles] = useState<File[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [notice, setNotice] = useState<{ text: string; ok: boolean } | null>(null);
  const [lightbox, setLightbox] = useState<Lightbox | null>(null);

  const loadCases = async () => {
    const { data } = await api.get<PortfolioCase[]>("/portfolio/me");
    setCases(data);
  };

  useEffect(() => { void loadCases(); }, []);

  useEffect(() => {
    const tg = getTelegramWebApp();
    if (!tg) return;
    tg.BackButton.show();
    const onBack = () => navigate("/handyman/profile");
    tg.BackButton.onClick(onBack);
    return () => {
      tg.BackButton.offClick(onBack);
      tg.BackButton.hide();
    };
  }, [navigate]);

  useEffect(() => {
    if (!lightbox) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((prev) => prev && prev.index < prev.images.length - 1 ? { ...prev, index: prev.index + 1 } : prev);
      if (e.key === "ArrowLeft") setLightbox((prev) => prev && prev.index > 0 ? { ...prev, index: prev.index - 1 } : prev);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightbox]);

  const categoryLabel = (slug: CategorySlug) => {
    switch (slug) {
      case CategorySlug.Plumber: return t.categories.plumber;
      case CategorySlug.Electrician: return t.categories.electrician;
      case CategorySlug.ApplianceRepair: return t.categories.applianceRepair;
      case CategorySlug.Cleaning: return t.categories.cleaning;
      case CategorySlug.Moving: return t.categories.moving;
      default: return t.categories.other;
    }
  };

  const inferContentType = (file: File): string => {
    if (file.type) return file.type;
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    const map: Record<string, string> = {
      jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png",
      webp: "image/webp", heic: "image/heic", heif: "image/heif",
    };
    return map[ext] ?? "image/jpeg";
  };

  const uploadFiles = async (selectedFiles: File[]) => {
    const uploadedUrls: string[] = [];
    for (const file of selectedFiles) {
      const contentType = inferContentType(file);
      const { data } = await api.post<PresignResponse>("/uploads/portfolio/presign", {
        fileName: file.name,
        contentType,
      });
      const uploadResponse = await fetch(data.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": contentType },
        body: file,
      });
      if (!uploadResponse.ok) throw new Error("upload_failed");
      uploadedUrls.push(data.publicUrl);
    }
    return uploadedUrls;
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setIsSaving(true);
    setNotice(null);
    try {
      const mediaUrls = files.length > 0 ? await uploadFiles(files) : [];
      await api.post("/portfolio/me/cases", {
        title,
        description,
        categorySlug,
        locationText: locationText || undefined,
        completedAt: completedAt ? new Date(completedAt).toISOString() : undefined,
        mediaUrls,
      });
      setTitle("");
      setDescription("");
      setLocationText("");
      setCompletedAt("");
      setFiles([]);
      setNotice({ text: t.portfolioSaved, ok: true });
      await loadCases();
    } catch {
      setNotice({ text: t.uploadError, ok: false });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (caseId: string) => {
    await api.delete(`/portfolio/me/cases/${caseId}`);
    await loadCases();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="border-b border-gray-100 bg-white px-6 py-5">
        <div className="mb-4 flex items-center justify-between">
          <button type="button" onClick={() => navigate("/handyman/profile")} className="text-sm font-semibold text-gray-500">
            {t.back}
          </button>
          <LanguageSwitcher />
        </div>
        <div className="text-2xl font-bold text-gray-900">{t.portfolioTitle}</div>
        <div className="mt-1 text-sm text-gray-500">{t.portfolioSubtitle}</div>
      </div>

      <div className="space-y-4 p-5">
        {/* Add case form */}
        <form className="rounded-2xl bg-white p-5 shadow-sm space-y-3" onSubmit={handleSubmit}>
          <div className="rounded-xl bg-amber-50 px-3 py-2.5 text-xs text-amber-700">{t.addCaseHint}</div>

          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t.portfolioCaseTitle} required
            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none focus:border-orange-400" />
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t.portfolioCaseDescription} rows={3} required
            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none focus:border-orange-400 resize-none" />

          {/* Category */}
          <select value={categorySlug} onChange={(e) => setCategorySlug(e.target.value as CategorySlug)}
            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none focus:border-orange-400">
            {CATEGORY_OPTIONS.map((slug) => (
              <option key={slug} value={slug}>{categoryLabel(slug)}</option>
            ))}
          </select>

          <input value={locationText} onChange={(e) => setLocationText(e.target.value)} placeholder={t.portfolioLocation}
            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none focus:border-orange-400" />
          <input type="datetime-local" value={completedAt} onChange={(e) => setCompletedAt(e.target.value)}
            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none focus:border-orange-400" />

          {/* File upload */}
          <label className="block cursor-pointer rounded-xl border-2 border-dashed border-gray-200 px-4 py-4 text-center text-sm text-gray-500 hover:border-orange-300 transition-colors">
            <div className="text-2xl mb-1">📸</div>
            {files.length > 0 ? `${files.length} ${t.uploadPhotos}` : t.uploadPhotos}
            <input type="file" accept="image/*" multiple onChange={(e) => setFiles(Array.from(e.target.files ?? []))} className="hidden" />
          </label>

          {notice ? (
            <div className={`rounded-xl px-4 py-3 text-sm font-medium ${notice.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
              {notice.text}
            </div>
          ) : null}

          <button type="submit" disabled={isSaving}
            className="w-full rounded-xl bg-orange-500 px-4 py-4 text-sm font-semibold text-white disabled:opacity-50 active:bg-orange-600 transition-colors">
            {isSaving ? t.loading : t.saveCase}
          </button>
        </form>

        {/* Existing cases */}
        {cases.length > 0 ? (
          <div className="rounded-2xl bg-white p-5 shadow-sm">
            <div className="mb-4 font-bold text-gray-900">{t.portfolioTitle}</div>
            <div className="space-y-4">
              {cases.map((item) => {
                const images = item.media.map((m) => m.fileUrl);
                return (
                  <div key={item.id} className="rounded-xl border border-gray-100 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900">{item.title}</div>
                        <div className="mt-0.5 text-xs text-orange-600">
                          {item.category ? (locale === "ru" ? item.category.nameRu || item.category.nameRo : item.category.nameRo) : "-"}
                        </div>
                      </div>
                      <button type="button" onClick={() => void handleDelete(item.id)}
                        className="shrink-0 rounded-xl border border-red-100 px-3 py-1.5 text-xs font-semibold text-red-500 active:bg-red-50 transition-colors">
                        {t.deleteCase}
                      </button>
                    </div>
                    <div className="mt-2 text-sm text-gray-600">{item.description}</div>
                    <div className="mt-1.5 text-xs text-gray-400">
                      📍 {item.locationText || "-"} · {item.completedAt ? new Date(item.completedAt).toLocaleDateString() : t.anytime}
                    </div>
                    {images.length > 0 ? (
                      <div className="mt-3 grid grid-cols-3 gap-2">
                        {images.map((url, i) => (
                          <button key={url} type="button"
                            onClick={() => setLightbox({ images, index: i })}
                            className="relative overflow-hidden rounded-xl active:opacity-80 transition-opacity">
                            <img src={url} alt={`${item.title} ${i + 1}`} className="h-24 w-full object-cover" />
                            {i === 2 && images.length > 3 ? (
                              <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-sm font-bold text-white">
                                +{images.length - 3}
                              </div>
                            ) : null}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>

      {/* Lightbox */}
      {lightbox ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95"
          onClick={() => setLightbox(null)}>
          <button type="button" onClick={() => setLightbox(null)}
            className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-xl text-white">
            ✕
          </button>
          <div className="absolute top-4 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white">
            {lightbox.index + 1} / {lightbox.images.length}
          </div>
          {lightbox.index > 0 ? (
            <button type="button"
              onClick={(e) => { e.stopPropagation(); setLightbox((prev) => prev ? { ...prev, index: prev.index - 1 } : null); }}
              className="absolute left-3 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-2xl text-white">
              ‹
            </button>
          ) : null}
          <img src={lightbox.images[lightbox.index]} alt=""
            className="max-h-[85vh] max-w-[92vw] rounded-xl object-contain"
            onClick={(e) => e.stopPropagation()} />
          {lightbox.index < lightbox.images.length - 1 ? (
            <button type="button"
              onClick={(e) => { e.stopPropagation(); setLightbox((prev) => prev ? { ...prev, index: prev.index + 1 } : null); }}
              className="absolute right-3 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-2xl text-white">
              ›
            </button>
          ) : null}
          {lightbox.images.length > 1 ? (
            <div className="absolute bottom-5 left-0 right-0 flex justify-center gap-1.5 px-4">
              {lightbox.images.map((_, i) => (
                <button key={i} type="button"
                  onClick={(e) => { e.stopPropagation(); setLightbox((prev) => prev ? { ...prev, index: i } : null); }}
                  className={`h-1.5 rounded-full transition-all ${i === lightbox.index ? "w-6 bg-white" : "w-1.5 bg-white/40"}`} />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
